import { useEffect } from 'react';

import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import { ARRIVAL_REASON } from '@/features/arrival/types';
import type { TranslationKey } from '@/i18n/dictionary';
import { useTranslation } from '@/i18n/useTranslation';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import { HANDOFF_CONTROL } from '@/services/handoff.contract';
import { useBack } from '@/shared/hooks/useBack';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import { RECORDED_COPY } from '../copy';
import { NextStepsBadge } from './NextStepsBadge';
import { TaskGroupList } from './TaskGroupList';

/**
 * ISS's three task groups, in the order they arrive [Figma `Section 1` / `Section 2` / `Group 3`]. A module
 * constant because the sequence is the meaning — the second literally ends "then when these are complete,".
 */
const TASK_GROUPS: readonly TranslationKey[] = [
  'finish.taskGroup1',
  'finish.taskGroup2',
  'finish.taskGroup3',
];

/**
 * The welcome-email hand-off and the paperwork message on it
 * [KAP-5a; Figma `New user / 12 · Your next steps` (was `10a`) — the prominence frames that draw decision
 * 0360's callout, 12860:29119 desktop · 29199 iPad · 29279 mobile, read live 2026-10-05. The base frames
 * 12446:28072 / 28147 / 28222 are the same screen without the callout; the per-element ids cited below are
 * theirs and still describe the same elements].
 *
 * **This is no longer the last screen.** Decision 0128 ruled the closeout into a separate screen on
 * 2026-09-08 — `11 · That's it for now`, which is `AllDoneScreen` and is KAP-2's. What used to close here
 * (the "wait for the representative" callout, the completed rail, and the action that freed the kiosk) has
 * moved there. This screen acknowledges and continues.
 *
 * One screen, two owners. **KAP-2 owns the mechanism** — that the screen exists at all, that it carries no
 * QR, link or redirect, that it never gates, and that the kiosk frees itself afterwards. **KAP-5 owns the
 * message**: ISS's own words about what is coming and where it lands.
 *
 * **No QR, no link, no redirect** [decision 0079]. The frame agrees — there is none on it. KAS-1's associate
 * card DOES carry one, for an already-placed associate looking at their own portal; the Operator scoped each
 * rule to its own screen, and the contradiction between the two approved specs is raised with ISS rather
 * than resolved here.
 *
 * **It informs and never embeds** [decision 0050]: no document, no field, no signature, no upload. And it
 * never gates [decision 0046] — nothing downstream is blocked, held or made conditional on this being read.
 *
 * **No badge photo** [decision 0078 cut it for launch].
 */
export function FinishScreen() {
  const t = useTranslation();
  const back = useBack();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  /*
   * ⭐ **The latest write failed, so this is the hand-off whatever came before it** [KPW-1c]. E4's *Get help*
   * lands here. A submission created EARLIER in the visit must not turn that into next steps for the other
   * job; E4 promised a person.
   */
  const handOff = useKioskSession(
    (state) => state.submission === undefined || state.submissionFailed,
  );
  const endCandidateSession = useKioskSession((state) => state.endCandidateSession);
  const goTo = useKioskSession((state) => state.goTo);
  const summonRepresentative = useKioskSession((state) => state.summonRepresentative);
  /*
   * ⭐ **Which control brought them here, read from the HELD arrival selection — never from this screen**
   * [KID-5d § Where it fits]. The no-submission branch below is shared: the *Ask for help from a representative*
   * row lands on it, and so do a failed write, the I-9 *Yes* and the pay card's *Request assistance*. Only the
   * row is held as `somethingElse`, so only the row names its control, and the api raises *asked for a rep* in
   * the state *asked for help* for it. Every other path names none and raises what it raised before.
   */
  const askedFromTheRow = useKioskSession(
    (state) => state.arrival?.reason === ARRIVAL_REASON.somethingElse,
  );

  /*
   * **The branch below PROMISES a person, so it queues one** [the Operator, 2026-09-11: *"si le estamos
   * diciendo que un representante la atendera y eso nunca va a pasar porque no entra a la queue le estamos
   * mintiendo"*]. *Ask for help from a representative* and the drug-test enquiry both land here, and until
   * now this screen told them to see a representative and put nobody on any list.
   *
   * ⚠️ **The condition is the render, not the arrival** — and that inversion is the whole fix. This effect
   * used to fire on the OTHER branch: a submission in the session enqueued *ready for recruiter* the moment
   * the screen mounted, so a candidate whose pay card registered successfully was queued for a recruiter he
   * never asked for, on a screen that offers him next steps and promises no one [Operator's queue,
   * 2026-09-11 07:52:33]. What a finished flow leaves behind is KPW-2's entry and it is raised where the
   * flow actually ends and where the candidate is told somebody is coming — the closeout, `AllDoneScreen`.
   *
   * No ref: `summonRepresentative` is idempotent for the whole candidate session, which also stops the I-9
   * route adding a *ready for recruiter* row beside its own *I-9 in person* one.
   */
  useEffect(() => {
    if (!handOff) return;
    summonRepresentative(undefined, askedFromTheRow ? HANDOFF_CONTROL.askForHelpRow : undefined);
  }, [askedFromTheRow, handOff, summonRepresentative]);

  /*
   * **No submission, no hand-off** [KAP-2's scenario; decision 0044].
   *
   * There is no welcome email and no ABLE account to point at, so offering the hand-off would be the dead
   * end 0044 removed, arriving by another route. This is also the SEAM: the submission is created by KPW-1,
   * which is still `draft` and not buildable, so in a real session this branch is the one that runs and
   * `submission` is always `undefined`. When KPW-1 ships, the only thing that changes is who sets the field
   * — nothing here.
   */
  if (handOff) {
    return (
      <KioskScreen>
        <KioskCard
          /*
           * ⚠️ **A way out, because this branch is a terminus and had none.**
           *
           * Without a footer `KioskCard` renders no controls at all, and the only other interactive thing
           * on the screen — the help button — is disabled. A candidate who arrived here was stuck until a
           * member of staff reloaded the browser, and the next person to walk up inherited their session.
           * That is the dead end BR-K7 forbids, and it became the DESIGNED end of the failed-write path
           * when the profile save landed.
           */
          footer={
            <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
              <Button onClick={endCandidateSession}>{t('routing.assistance.startOver')}</Button>
            </div>
          }
          /*
           * **Back, because most people who land here did not fail at anything** [Operator, 2026-09-02].
           *
           * This branch is reached two very different ways. One is a write that failed, where starting over
           * is the only honest offer. The other is an ARRIVAL REASON with no screen of its own yet — the
           * drug test, "talk to someone" — where the person simply picked a menu item and has every reason
           * to want the menu back. Sending both to a terminus whose only control wipes the session made the
           * second group pay for the first group's failure.
           *
           * `useBack` already returns them to the menu when that is where they came from, and ends the
           * session otherwise, so one control serves both without a branch here.
           */
          header={
            <>
              <BackLink onClick={back} />
              <StatusBadge label={t('badge.kiosk')} />
            </>
          }
        >
          <div className="flex w-full flex-col items-start gap-6 pb-8">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {t('finish.handOff.heading')}
            </h1>
            <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
              {t('finish.handOff.body')}
            </p>
          </div>
        </KioskCard>
      </KioskScreen>
    );
  }

  return (
    <KioskScreen>
      <KioskCard
        footer={
          /*
            The frame draws this action full width [12446:28091, 720 of a 768 card] and its label reads
            "I understand" — an acknowledgement, not a farewell. It no longer ends the session: the flow
            continues to the closeout, which is the screen that frees the kiosk [decision 0128].

            ⚠️ The frame also draws the I-9 screen's question — "Would you like a staff member to help you
            today?" — above this single button [12446:28089]. A question with one button that says "I
            understand" cannot be answered; it is a footer component reused without its second control, so
            it is NOT built. Owed back to the designer.
          */
          <div className="flex w-full flex-col">
            <Button
              fullWidth
              onClick={() => {
                goTo(SCREEN.allDone);
              }}
            >
              {t('finish.iUnderstand')}
            </Button>
          </div>
        }
        /*
          No rail on this screen. `10a · Your next steps` draws none [12446:28072 / 28147 / 28222] — it
          appears, complete, on the closeout that follows [decision 0128]. NO story specifies the rail at
          all: five named stages of the candidate journey is a product statement the Ground Truth does not
          record. Built to the design and reported.
        */
        header={
          <>
            {/*
              The frame draws a Back control here even though KAP-2 says the kiosk experience finishes on
              this screen. Wired to END the session rather than to step backwards into a completed flow:
              going back into it would contradict the story, and removing a control the frame draws would
              contradict the design. Ending the session is what both of them want.

              Passed as two siblings, not wrapped in a flex row of their own — `KioskCard` lays the header
              out to the frame's `Left`/`Right` slots, and a wrapper here re-did that job at a different
              height.
            */}
            <BackLink onClick={back} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
      >
        {/*
          A UNIFORM 12px between every block and 40px under the last one — the frame's `Main` measured
          child by child [12446:28080, read live 2026-09-08]: icon 0–56, heading 68–96, intro 108–156,
          lead-in 168–192, the three rows 204–328, callout 340–432, and `Main` closes at 472.

          There is no rhythm to interpret: it is 12 eight times. This carried 24 between the groups and 8
          inside the second one, which pushed the lead-in 13px down, the rows 9px, and the callout 5px, and
          left the block 8px short of the frame's bottom margin.
        */}
        <div className="flex w-full flex-col items-start gap-3 pb-10">
          {/* The frame centres the intro block and left-aligns everything below it. */}
          <div className="flex w-full flex-col items-center gap-3">
            {/*
              The teal DOCUMENT badge, not the green success mark [12446:28081]. The success mark is the
              closeout's, and borrowing it here said "you are finished" one screen before the candidate is
              — on the screen whose whole job is to explain what is still coming.
            */}
            <NextStepsBadge />
            <h1
              className="kiosk-heading-focus w-full text-center text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {t('finish.heading')}
            </h1>
            {/*
              ISS's English, word for word from the frame; its Spanish is machine-translated for ISS to
              review [0436; 0328 rule 1; OQ-134] — see `../copy.ts`. It names the WELCOME email, the only
              actionable one of the two that land at submission [0080].
            */}
            <p className="w-full text-center text-sm leading-6 text-[var(--muted-foreground)]">
              {t('finish.intro')}
            </p>
          </div>

          <div className="flex w-full flex-col items-start gap-3">
            {/*
              ⭐ Decision 0360's string — the ONE Rootstrap-written sentence on this screen, provisional
              until ISS words it [0350], and marked as ours in `RECORDED_COPY`. The frame draws it as the
              `Tone=Info` callout directly under the welcome-email paragraph, on the same 12px rhythm, and
              left-aligned like everything below it [12860:29180 / 29260 / 29339].
            */}
            <Callout tone="info">{t(RECORDED_COPY.personalDevice)}</Callout>
            {/*
              14/24 regular and CENTRED — a sentence in the same voice as the intro above it [12446:28246],
              not the uppercase section heading this reused. Rendered as a heading, its trailing em dash
              orphaned onto a line of its own ("…YOU WILL COMPLETE" / "—"), which is how a sentence looks
              when it is dressed as a label.
            */}
            <p className="w-full text-center text-sm leading-6 text-[var(--muted-foreground)]">
              {t('finish.taskGroupsLabel')}
            </p>
            <TaskGroupList items={TASK_GROUPS.map((key) => t(key))} />
            {/*
              The timing note is a `Tone=Brand` CALLOUT [12446:28086 / 12860:29215], not the plain paragraph
              it was — and it ends at "…for you to complete.": the recruiter sentence is struck [0128].
            */}
            <Callout tone="brand">{t('finish.cadence')}</Callout>
            {/*
              ⚠️ **No credentials paragraph.** ISS deleted *"And be sure to save your credentials to our
              Talent Portal…"* from the frame [0128], and with it KAP-5's one conditional — so this screen
              renders the same for every candidate [KAP-5c]. The credential flag still travels on the
              lookup's diagnosis [0233; 0440] and this screen does not read it — no branch, no hidden region,
              no variant string, and no re-read on mount, focus or retry [0029].
            */}
          </div>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
